/* eslint-disable quotes */
'use client';

import { useState, useEffect } from 'react';
import { useLanguage } from '@/app/context/language-context';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { ArrowRight } from 'lucide-react';

export default function CallToAction() {
  const { t } = useLanguage();
  const router = useRouter();
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setBgLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // preload slightly before visible
    );

    const section = document.querySelector('#lazy-bg');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <motion.section
      className='py-16 px-4 relative overflow-hidden'
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div
        id='lazy-bg'
        className='absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-20 dark:opacity-10'
        style={{
          backgroundImage: bgLoaded
            ? "url('https://images.x-space.ae/assets/images/website_image/home_page_1.jpg')"
            : 'none',
        }}
      />

      <div className='max-w-7xl mx-auto relative z-10'>
        <div className='bg-white dark:bg-gray-800 p-10 rounded-2xl shadow-xl'>
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8 items-center'>
            <div className='lg:col-span-2'>
              <motion.h2
                className='text-3xl font-bold text-gray-900 dark:text-white mb-4'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {t('readyToFindProperty')}
              </motion.h2>
              <motion.p
                className='text-gray-600 dark:text-gray-400 text-lg'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {t('readyToFindPropertyDescription')}
              </motion.p>
            </div>

            <motion.div
              className='flex flex-col space-y-4'
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <motion.button
                className='w-full py-4 bg-primarycolor text-white rounded-md hover:bg-secondarycolor hover:text-black transition-colors flex items-center justify-center space-x-2 shadow-lg'
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => router.push('/contact-us')}
              >
                <span> {t('contactUsNow')}</span>
                <ArrowRight size={16} />
              </motion.button>

              <motion.button
                className='px-6 py-3 bg-white dark:bg-gray-800 text-primarycolor dark:text-secondarycolor dark:border-secondarycolor border-2 border-primarycolor hover:border-secondarycolor dark:hover:border-primarycolor dark:border-primarycolor rounded-md font-medium hover:bg-secondarycolor hover:text-black dark:hover:bg-primarycolor dark:hover:text-white transition-colors'
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => router.push('/properties/buy')}
              >
                {t('viewMoreProperties')}
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
